/**
 * Het tabblad Zoekopdrachten: wat de extensie periodiek afloopt, en de plek om
 * dat bij te stellen. De kop van het scherm komt als prop binnen, net als bij
 * Importeren, zodat de tabbladen en de knop op één regel staan.
 */
import {
  FUNDA_DAGPLAFOND_MAX,
  FUNDA_DAGPLAFOND_MIN,
  FUNDA_VENSTER_PAGINAS_MAX,
  FUNDA_VENSTER_PAGINAS_MIN,
  FUNDA_VENSTER_TIJDEN_MAX,
  type FundaRust,
  type Zoekopdracht,
} from '@mamimap/shared';
import { Play, Plus } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { Melding } from '@/components/Melding';
import {
  useBewerkZoekopdracht,
  useFundaRust,
  useGebieden,
  useZetFundaDagplafond,
  useZetFundaVenster,
  useZoekopdrachten,
} from '@/lib/api/queries';
import { Link, ROUTES } from '@/lib/router';
import { Blokkadelog } from './Blokkadelog';
import {
  dagplafondWaarschuwing,
  fundaRustRegel,
  gebiedNamen,
  leesTijden,
  vensterRegel,
} from './stand';
import { Zoekgebied } from './Zoekgebied';
import { ZoekopdrachtPaneel } from './ZoekopdrachtPaneel';
import { ZoekopdrachtTabel } from './ZoekopdrachtTabel';

type Paneel = { soort: 'dicht' } | { soort: 'nieuw' } | { soort: 'bewerk'; id: string };

export function Zoekopdrachten({ kop }: { kop: ReactNode }) {
  const lijst = useZoekopdrachten();
  const rust = useFundaRust();
  const gebieden = useGebieden();
  const bewerk = useBewerkZoekopdracht();
  const [paneel, zetPaneel] = useState<Paneel>({ soort: 'dicht' });

  const namen = useMemo(() => gebiedNamen(gebieden.data), [gebieden.data]);
  const rijen = lijst.data ?? [];
  const bestaand =
    paneel.soort === 'bewerk' ? (rijen.find((rij) => rij.id === paneel.id) ?? null) : null;

  function wissel(zoekopdracht: Zoekopdracht, actief: boolean) {
    bewerk.mutate({ id: zoekopdracht.id, wijziging: { actief } });
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-4">
        {kop}
        <div className="flex items-center gap-2">
          <Link naar={ROUTES.ronde} className="knop-stil">
            <Play className="h-3.5 w-3.5" aria-hidden="true" />
            Funda-ronde
          </Link>
          <button
            type="button"
            className="knop"
            aria-expanded={paneel.soort === 'nieuw'}
            onClick={() =>
              zetPaneel(paneel.soort === 'nieuw' ? { soort: 'dicht' } : { soort: 'nieuw' })
            }
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            Nieuwe zoekopdracht
          </button>
        </div>
      </div>

      {rust.data ? <FundaRustRegel key={rust.data.dagplafond} stand={rust.data} /> : null}
      {rust.data ? (
        <VensterRegel
          key={`${rust.data.venster.tijden.join(',')}|${rust.data.venster.paginas}`}
          stand={rust.data}
        />
      ) : null}
      <Blokkadelog />

      {paneel.soort === 'nieuw' || bestaand ? (
        <ZoekopdrachtPaneel
          key={bestaand?.id ?? 'nieuw'}
          bestaand={bestaand}
          alle={rijen}
          onKlaar={() => zetPaneel({ soort: 'dicht' })}
        />
      ) : null}

      <Zoekgebied />

      {lijst.error ? <Melding fout={lijst.error} /> : null}
      {bewerk.error ? <Melding fout={bewerk.error} /> : null}

      {rijen.length > 0 ? (
        <ZoekopdrachtTabel
          rijen={rijen}
          namen={namen}
          onBewerk={(rij) => zetPaneel({ soort: 'bewerk', id: rij.id })}
          onWissel={wissel}
          bezigId={bewerk.isPending ? (bewerk.variables?.id ?? null) : null}
        />
      ) : null}

      {lijst.data?.length === 0 ? (
        <p className="border border-line bg-surface-strong px-3 py-3 text-[12px] text-muted">
          Nog geen zoekopdrachten. Maak er hier een uit buurten, of leg er een vast op Funda met de
          knop van de extensie.
        </p>
      ) : null}
    </div>
  );
}

/**
 * De regel over de stand van de vanger (MA-28), met sinds MA-32 het dagplafond
 * ernaast als getal met Opslaan. Tekst en een klein veld, geen kader: het is
 * een stand die je af en toe bijstelt. De ouder geeft `key={dagplafond}`, zodat
 * het veld na opslaan (of een andere pc die het zette) weer op de bewaarde
 * waarde begint.
 */
function FundaRustRegel({ stand }: { stand: FundaRust }) {
  const zet = useZetFundaDagplafond();
  const [tekst, zetTekst] = useState(String(stand.dagplafond));

  const waarde = Number(tekst);
  const geldig =
    tekst.trim() !== '' &&
    Number.isInteger(waarde) &&
    waarde >= FUNDA_DAGPLAFOND_MIN &&
    waarde <= FUNDA_DAGPLAFOND_MAX;
  const gewijzigd = geldig && waarde !== stand.dagplafond;
  const waarschuwing = dagplafondWaarschuwing(geldig ? waarde : stand.dagplafond);

  return (
    <div className="flex flex-col gap-1 text-[12.5px] text-ink-soft">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <p className="cijfers" aria-live="polite">
          {fundaRustRegel(stand)}
        </p>
        <form
          className="flex items-center gap-1.5 text-[12px] text-muted"
          onSubmit={(event) => {
            event.preventDefault();
            if (gewijzigd && !zet.isPending) zet.mutate(waarde);
          }}
        >
          <label className="flex items-center gap-1.5">
            Plafond
            <input
              className="veld cijfers w-16 text-right"
              inputMode="numeric"
              aria-label={`Dagplafond in pagina's, ${FUNDA_DAGPLAFOND_MIN} tot ${FUNDA_DAGPLAFOND_MAX}`}
              aria-invalid={!geldig}
              value={tekst}
              onChange={(event) => zetTekst(event.target.value)}
            />
          </label>
          <button type="submit" className="knop-stil" disabled={!gewijzigd || zet.isPending}>
            {zet.isPending ? 'Opslaan ...' : 'Opslaan'}
          </button>
        </form>
      </div>
      {waarschuwing ? <p className="text-[12px] text-muted">{waarschuwing}</p> : null}
      {zet.error ? <Melding fout={zet.error} /> : null}
    </div>
  );
}

/**
 * De vaste tijden (MA-46): wanneer de vanger zijn lading ophaalt en hoeveel
 * pagina's dat per keer is. Zelfde vorm als het dagplafond erboven: een regel
 * met de stand, twee kleine velden en Opslaan.
 */
function VensterRegel({ stand }: { stand: FundaRust }) {
  const zet = useZetFundaVenster();
  const [tijdenTekst, zetTijdenTekst] = useState(stand.venster.tijden.join(', '));
  const [paginasTekst, zetPaginasTekst] = useState(String(stand.venster.paginas));

  const tijden = leesTijden(tijdenTekst);
  const paginas = Number(paginasTekst);
  const tijdenGeldig = tijden !== null && tijden.length <= FUNDA_VENSTER_TIJDEN_MAX;
  const paginasGeldig =
    Number.isInteger(paginas) &&
    paginas >= FUNDA_VENSTER_PAGINAS_MIN &&
    paginas <= FUNDA_VENSTER_PAGINAS_MAX;
  const gewijzigd =
    tijdenGeldig &&
    paginasGeldig &&
    (tijden.join(',') !== stand.venster.tijden.join(',') || paginas !== stand.venster.paginas);

  return (
    <div className="flex flex-col gap-1 text-[12.5px] text-ink-soft">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <p className="cijfers" aria-live="polite">
          {vensterRegel(stand)}
        </p>
        <form
          className="flex items-center gap-1.5 text-[12px] text-muted"
          onSubmit={(event) => {
            event.preventDefault();
            if (gewijzigd && tijden && !zet.isPending) zet.mutate({ tijden, paginas });
          }}
        >
          <label className="flex items-center gap-1.5">
            Tijden
            <input
              className="veld cijfers w-28"
              aria-label="Vaste tijden, bijvoorbeeld 12:15, 19:30"
              aria-invalid={!tijdenGeldig}
              value={tijdenTekst}
              onChange={(event) => zetTijdenTekst(event.target.value)}
            />
          </label>
          <label className="flex items-center gap-1.5">
            Per keer
            <input
              className="veld cijfers w-14 text-right"
              inputMode="numeric"
              aria-label={`Pagina's per keer, ${FUNDA_VENSTER_PAGINAS_MIN} tot ${FUNDA_VENSTER_PAGINAS_MAX}`}
              aria-invalid={!paginasGeldig}
              value={paginasTekst}
              onChange={(event) => zetPaginasTekst(event.target.value)}
            />
          </label>
          <button type="submit" className="knop-stil" disabled={!gewijzigd || zet.isPending}>
            {zet.isPending ? 'Opslaan ...' : 'Opslaan'}
          </button>
        </form>
      </div>
      {zet.error ? <Melding fout={zet.error} /> : null}
    </div>
  );
}
